import { Controller } from "@hotwired/stimulus"

const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const WEEKS = ['first', 'second', 'third', 'fourth', 'last']

export default class extends Controller {
  static targets = ["frequency", "weeklyField", "monthlyField", "monthlyRules", "monthlyRuleTemplate", "weeklyCheckbox", "weeklyPill", "timeField"]

  connect() {
    this.ruleIndex = this.monthlyRulesTarget.querySelectorAll('[data-rule]').length
    this.toggle()
    this.syncWeeklyPills()
  }

  toggle() {
    const frequency = this.frequencyTarget.value
    this.weeklyFieldTarget.classList.toggle("hidden", frequency !== "weekly")
    this.monthlyFieldTarget.classList.toggle("hidden", frequency !== "monthly")
    if (this.hasTimeFieldTarget) {
      this.timeFieldTarget.classList.toggle("hidden", !frequency)
    }
  }

  toggleDay() {
    this.syncWeeklyPills()
  }

  syncWeeklyPills() {
    this.weeklyCheckboxTargets.forEach((cb, i) => {
      const pill = this.weeklyPillTargets[i]
      if (!pill) return
      if (cb.checked) {
        pill.classList.add('bg-violet-100', 'text-violet-700', 'border-violet-300')
        pill.classList.remove('bg-white', 'text-gray-500', 'border-gray-200')
      } else {
        pill.classList.remove('bg-violet-100', 'text-violet-700', 'border-violet-300')
        pill.classList.add('bg-white', 'text-gray-500', 'border-gray-200')
      }
    })
  }

  addRule() {
    const rule = this.buildRuleElement(this.ruleIndex++)
    this.monthlyRulesTarget.appendChild(rule)
    this.updateRemoveButtons()
  }

  removeRule(event) {
    event.currentTarget.closest('[data-rule]').remove()
    this.updateRemoveButtons()
  }

  toggleRuleType(event) {
    const ruleEl = event.currentTarget.closest('[data-rule]')
    const type = event.currentTarget.value
    const daySelect = ruleEl.querySelector('[data-day-field]')
    daySelect.classList.toggle('hidden', type === 'first_day' || type === 'last_day')
    ruleEl.querySelector('[data-week-field]').classList.toggle('hidden', type !== 'weekday')
    this.rebuildDayOptions(daySelect, type)
  }

  rebuildDayOptions(select, type) {
    const options = type === 'day'
      ? Array.from({ length: 28 }, (_, i) => [this.ordinal(i + 1), String(i + 1)])
      : DAY_LABELS.map((label, i) => [label, DAYS_OF_WEEK[i]])
    select.innerHTML = ''
    options.forEach(([label, value]) => {
      const opt = document.createElement('option')
      opt.value = value
      opt.textContent = label
      select.appendChild(opt)
    })
  }

  ordinal(n) {
    const s = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
  }

  updateRemoveButtons() {
    const rules = this.monthlyRulesTarget.querySelectorAll('[data-rule]')
    rules.forEach(rule => {
      const btn = rule.querySelector('[data-action="schedule-form#removeRule"]')
      if (btn) btn.classList.toggle('hidden', rules.length <= 1)
    })
  }

  buildRuleElement(index) {
    const wrapper = document.createElement('div')
    wrapper.setAttribute('data-rule', '')
    wrapper.className = 'flex flex-wrap items-center gap-1.5 py-2'

    const typeSelect = this.buildSelect(`recurrence[${index}][type]`, [
      ['Specific day', 'day'],
      ['First day', 'first_day'],
      ['Last day', 'last_day'],
      ['Specific weekday', 'weekday'],
    ], 'weekday')

    typeSelect.setAttribute('data-action', 'change->schedule-form#toggleRuleType')
    typeSelect.className = 'rounded-lg border-gray-300 text-xs focus:border-violet-500 focus:ring-violet-500'

    const weekSelect = this.buildSelect(`recurrence[${index}][week]`,
      WEEKS.map(w => [w.charAt(0).toUpperCase() + w.slice(1), w]), 'first')
    weekSelect.className = 'rounded-lg border-gray-300 text-xs focus:border-violet-500 focus:ring-violet-500'
    weekSelect.setAttribute('data-week-field', '')

    const daySelect = this.buildSelect(`recurrence[${index}][day]`,
      DAY_LABELS.map((label, i) => [label, DAYS_OF_WEEK[i]]), 'monday')
    daySelect.className = 'rounded-lg border-gray-300 text-xs focus:border-violet-500 focus:ring-violet-500'
    daySelect.setAttribute('data-day-field', '')

    const removeBtn = document.createElement('button')
    removeBtn.type = 'button'
    removeBtn.setAttribute('data-action', 'schedule-form#removeRule')
    removeBtn.className = 'text-red-400 hover:text-red-600 text-sm cursor-pointer'
    removeBtn.innerHTML = '&times;'

    wrapper.append(typeSelect, weekSelect, daySelect, removeBtn)
    return wrapper
  }

  buildSelect(name, options, selected) {
    const select = document.createElement('select')
    select.name = name
    options.forEach(([label, value]) => {
      const opt = document.createElement('option')
      opt.value = value
      opt.textContent = label
      if (value === selected) opt.selected = true
      select.appendChild(opt)
    })
    return select
  }
}
